// ── Gap-Based Travel Estimates ──
// Pure travel-estimate util for a derived DayView. Replaces the deleted
// auto-time cascade's travelSegments/timeOverruns outputs. Informational
// only — never blocks a mutation.

import type { DayBlock, DayView, POI } from "@poi-planner/shared";
import { estimateTravel, type Coords, type TravelEstimate } from "./geo";
import { blockEndTime, blockStartTime } from "./dayViews";
import { minutesBetween } from "./zonedTime";
import { hasUsableCoordinates } from "./dayMapPois";

export interface TravelSegment {
  fromBlockId: string;
  toBlockId: string;
  estimate: TravelEstimate;
  /** Actual gap between the blocks (next.start − prev.end), in minutes */
  gapMinutes: number;
  /** True when the estimated travel time exceeds the actual gap */
  overrun: boolean;
}

function poiCoords(poi: POI | undefined): Coords | null {
  if (!poi || !hasUsableCoordinates(poi)) return null;
  return { lat: poi.lat, lng: poi.lng };
}

/**
 * Coordinates for a block. A POI block uses its POI's location; a note uses
 * its end-POI's coords, falling back to its start-POI's. A note with neither
 * is coordinate-less and suppresses the segments on both sides — a misleading
 * chip is worse than none.
 */
function blockCoords(block: DayBlock, poiMap: Map<string, POI>): Coords | null {
  if (block.type === "poi") {
    return poiCoords(poiMap.get(block.scheduledPOI.poiId));
  }
  return (
    poiCoords(
      block.note.endPoiId ? poiMap.get(block.note.endPoiId) : undefined,
    ) ??
    poiCoords(
      block.note.startPoiId ? poiMap.get(block.note.startPoiId) : undefined,
    )
  );
}

/**
 * Computes travel segments between consecutive blocks of a day. A segment is
 * emitted only when both neighbouring blocks have coordinates and the
 * distance is drivable (estimateTravel returns null beyond that).
 */
export function computeTravelSegments(
  dayView: DayView,
  pois: POI[],
): TravelSegment[] {
  const poiMap = new Map(pois.map((p) => [p.id, p]));
  const segments: TravelSegment[] = [];

  // DayView blocks are already sorted by start instant
  for (let i = 0; i < dayView.blocks.length - 1; i++) {
    const prev = dayView.blocks[i];
    const next = dayView.blocks[i + 1];

    const from = blockCoords(prev, poiMap);
    const to = blockCoords(next, poiMap);
    if (!from || !to) continue;

    const estimate = estimateTravel(from, to);
    if (!estimate) continue;

    const gapMinutes = minutesBetween(blockEndTime(prev), blockStartTime(next));
    // Overlapping blocks have no travel gap to annotate — a chip on top of
    // the cascade is noise, not information
    if (gapMinutes < 0) continue;
    segments.push({
      fromBlockId: prev.id,
      toBlockId: next.id,
      estimate,
      gapMinutes,
      overrun: estimate.travelMinutes > gapMinutes,
    });
  }

  return segments;
}
